import { useMemo, useRef } from "react";
import NotificationItem from "./NotificationItem";
import useInfiniteScroll from "../../hooks/notifications/useInfiniteScroll";
import { useNotifications } from "../../hooks/notifications/useNotifications";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";

/**
 * VirtualizedNotificationList Component
 *
 * High-performance virtualized list for large numbers of notifications
 * Uses react-window for efficient rendering of only visible items
 */
const VirtualizedNotificationList = ({
  height = 600,
  itemHeight = 80,
  showCheckbox = false,
  onNotificationClick,
  className = "",
}) => {
  const { selectedNotifications, queryParams } = useNotificationQueryContext();
  const { data: notificationsData } = useNotifications(queryParams);

  const notifications = notificationsData?.data?.notifications || [];

  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Set up infinite scroll
  const { loadMore, isLoading, hasMore } = useInfiniteScroll({
    threshold: itemHeight * 3, // Load more when 3 items from bottom
    enabled: true,
    containerRef,
  });

  // Calculate total height including loading indicator
  const totalHeight = useMemo(() => {
    let calculatedHeight = notifications.length * itemHeight;
    if (isLoading || hasMore) {
      calculatedHeight += itemHeight; // Add space for loading indicator
    }
    return Math.min(calculatedHeight, height);
  }, [notifications.length, itemHeight, height, isLoading, hasMore]);

  return (
    <div
      ref={containerRef}
      className={`virtualized-notification-list ${className}`}
    >
      {notifications.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="text-gray-500 mb-2">No notifications found</div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto" />
            )}
          </div>
        </div>
      ) : (
        <div
          ref={listRef}
          className="notification-virtual-list overflow-y-auto"
          style={{ height: totalHeight }}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            const scrollBottom = scrollTop + clientHeight;
            const threshold = itemHeight * 3;

            if (
              scrollHeight - scrollBottom < threshold &&
              hasMore &&
              !isLoading
            ) {
              loadMore();
            }
          }}
        >
          {notifications.map((notification, index) => (
            <div key={notification._id} style={{ height: itemHeight }}>
              <NotificationItem
                notification={notification}
                showCheckbox={showCheckbox}
                isSelected={selectedNotifications.includes(notification._id)}
                onClose={() => onNotificationClick?.(notification)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Loading indicator at bottom */}
      {isLoading && notifications.length > 0 && (
        <div className="flex items-center justify-center py-4 border-t border-gray-100">
          <div className="inline-flex items-center text-sm text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2" />
            Loading more notifications...
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualizedNotificationList;
